import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useProfileStore } from '@/stores/profileStore';

/** Query parameter that pins a page to one company profile. */
export const PROFILE_PARAM = 'profile';

/**
 * Keep the active profile and the URL in sync.
 *
 * The active profile used to live only in a Zustand store, which meant two
 * people opening the same link saw different clients' data — and there was no
 * way to send "here is Genetico's rank tracking" to a colleague. For an agency
 * running several profiles that is a real gap.
 *
 * `?profile=<id>` wins on load (an incoming link decides what you look at), and
 * switching profiles rewrites the parameter so the address bar always describes
 * what is on screen.
 *
 * Deliberately a query parameter rather than a path segment (`/app/p/:id/...`).
 * The path version is tidier, but it would rewrite every route and every
 * internal link in the app for the same shareability. If the router is ever
 * restructured, this is the piece to replace.
 *
 * Note on dependencies: this reads `location.search` (a string) rather than the
 * object from `useSearchParams`, which react-router rebuilds on every render —
 * depending on that object re-ran the write effect each render and span the
 * component forever.
 */
export function useProfileFromUrl(): void {
  const location = useLocation();
  const navigate = useNavigate();

  const profiles = useProfileStore((s) => s.profiles);
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const setActiveProfile = useProfileStore((s) => s.setActiveProfile);

  const search = location.search;
  const requestedId = new URLSearchParams(search).get(PROFILE_PARAM);
  const activeId = activeProfile?.id;

  // Read: an incoming link decides which profile you are looking at.
  useEffect(() => {
    if (!requestedId || requestedId === activeId) return;
    const requested = profiles.find((p) => p.id === requestedId);
    // Unknown id: leave the current profile alone rather than blanking the
    // page. It usually means a link from someone else's account.
    if (requested) setActiveProfile(requested);
  }, [requestedId, profiles, activeId, setActiveProfile]);

  // Write: make the address bar describe what is on screen, so a link copied
  // from any page carries its profile. `replace` because switching profiles is
  // not a step the back button should have to walk through.
  useEffect(() => {
    if (!activeId) return;
    const params = new URLSearchParams(search);
    const inUrl = params.get(PROFILE_PARAM);
    if (inUrl === activeId) return;

    // The URL names a profile we can honour — the read effect above owns this
    // and is about to switch to it. Writing here would fight it: React runs
    // both effects in the same commit, so this one still sees the PREVIOUS
    // active profile and would navigate straight back, forever.
    if (inUrl && profiles.some((p) => p.id === inUrl)) return;

    params.set(PROFILE_PARAM, activeId);
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
  }, [location.pathname, search, activeId, profiles, navigate]);
}
